import { yupResolver } from '@hookform/resolvers/yup'
import { DataTablePagination } from 'components/common'
import { usePageAccess, useQuery } from 'hooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useLocation } from 'react-router-dom'
import { PickerMapping, PickerMappingsData } from 'types/pickerMap'
import {
  deletePickerMappingApi,
  getPickerMappingsApi,
  updatePickerMappingApi,
} from 'utils/api/pickerEmpMapApi'
import { handleError } from 'utils/helpers/handleError'
import { updatePickerMapSchema } from 'utils/schema/pickerEmpMapSchema'
import PickerMapItem from './pickerMapItem'
import PickerMapViewRow from './pickerMapViewRow'

type FormValues = {
  mappings: PickerMapping[]
}

const PickerEmpList = ({ refetch }: { refetch: boolean }) => {
  const [data, setData] = useState<PickerMappingsData>({ docs: [] })
  const [isLoading, setIsLoading] = useState(true)

  const { isEditor } = usePageAccess()

  const { search } = useLocation()
  const { getAllQueries } = useQuery()

  const methods = useForm<FormValues>({
    resolver: yupResolver(updatePickerMapSchema),
    defaultValues: { mappings: [] },
    mode: 'onBlur',
  })

  const { control, getValues } = methods

  const { fields, replace } = useFieldArray({
    control,
    name: 'mappings',
  })

  // Rows as loaded, by position; a row is identified by pickerId + pickerName
  const originalDataRef = useRef<PickerMapping[]>([])

  const fetchMappings = useCallback(
    async (omitLoading: boolean = false) => {
      try {
        !omitLoading && setIsLoading(true)
        const { limit, page } = getAllQueries()
        const { data: pickerMapData } = await getPickerMappingsApi({
          limit,
          page,
        })
        setData(pickerMapData)
        replace(pickerMapData?.docs || [])

        originalDataRef.current = pickerMapData?.docs || []
      } catch {
        setData({ docs: [] })
      } finally {
        !omitLoading && setIsLoading(false)
      }
    },
    [getAllQueries, replace],
  )

  const handleRowSave = useCallback(
    async (index: number) => {
      const toastId = toast.loading('Updating Picker Mapping...', {
        duration: Infinity,
      })

      const currentRow = getValues(`mappings.${index}`)
      const originalRow = originalDataRef.current[index]

      const isModified =
        currentRow.pickerName !== originalRow.pickerName ||
        (currentRow.pickerId ?? '') !== (originalRow.pickerId ?? '')

      if (!isModified) {
        toast.dismiss(toastId)
        return
      }

      try {
        await updatePickerMappingApi(originalRow, {
          pickerName: currentRow.pickerName,
          pickerId: currentRow.pickerId,
        })

        await fetchMappings(true)

        toast.success('Picker Mapping updated successfully', {
          id: toastId,
          duration: 3000,
        })
      } catch (error) {
        handleError(error, toastId, {
          default: 'Error updating Picker Mapping',
          PICKER_NAME_ALREADY_EXIST: 'Picker Name already exists',
          PICKER_ID_NOT_FOUND: 'Picker ID not found in Order Tracker',
        })
      }
    },
    [getValues, fetchMappings],
  )

  const handleDelete = useCallback(
    async (index: number) => {
      const toastId = toast.loading('Deleting Mapping...', {
        duration: Infinity,
      })

      try {
        await deletePickerMappingApi(originalDataRef.current[index])
        await fetchMappings(true)
        toast.success('Mapping deleted successfully', {
          id: toastId,
          duration: 3000,
        })
      } catch (error) {
        handleError(error, toastId)
      }
    },
    [fetchMappings],
  )

  useEffect(() => {
    fetchMappings()
  }, [fetchMappings, search, refetch])

  return (
    <FormProvider {...methods}>
      <div className="mt-5">
        <h3 className="title">Picker - Employee Mappings</h3>

        <div
          className={`${
            isEditor ? '' : ' rounded-lg border border-gray-200 bg-white'
          }  `}
        >
          <div
            className={`grid bg-gray-200 px-4 py-2 text-gray-800 ${
              isEditor ? 'mb-2 grid-cols-4 rounded-lg ' : 'grid-cols-3'
            }  gap-4 font-bold`}
          >
            <div>Picker Name</div>
            <div>Picker ID</div>
            <div>Employee ID</div>
            {isEditor && <div className="text-center">Action</div>}
          </div>

          {isLoading ? (
            <PickerMapViewRow
              key={'loading'}
              field={{ pickerId: '', pickerName: 'Loading...' }}
            />
          ) : fields.length > 0 ? (
            <>
              <div className="h-[520px] overflow-y-auto">
                {fields.map((field, index) =>
                  isEditor ? (
                    <PickerMapItem
                      key={field.id}
                      index={index}
                      originalData={originalDataRef.current[index]}
                      onSave={handleRowSave}
                      onDelete={handleDelete}
                    />
                  ) : (
                    <PickerMapViewRow key={field.id} field={field} />
                  ),
                )}
              </div>

              <DataTablePagination
                parentClassName="px-4 border-t border-gray-200 py-3 my-0"
                currentPage={data?.page}
                totalPages={data?.totalPages}
                hasNextPage={data?.hasNextPage}
                hasPrevPage={data?.hasPrevPage}
                limit={data?.limit}
                nextPage={data?.nextPage}
                prevPage={data?.prevPage}
                disabled={isLoading}
              />
            </>
          ) : (
            <p className="w-full">No Mappings Found</p>
          )}
        </div>
      </div>
    </FormProvider>
  )
}

export default PickerEmpList
